import { useState, useContext, useEffect } from "react";

import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Input, Modal, message } from "antd";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import { RoleContext, useIsReviewer } from "../context/RoleContext";
import DfnsService from "../services/DfnsService";
import UserService from "../services/UserService";
import { isEthereumAddress } from "../utils";
import { SECONDARY_WALLET_ENABLED } from "../utils/featureFlags";
import { waitForIndexer } from "../utils/indexerWait";

const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";

function CreateDigitalId({ service }) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const navigate = useNavigate();
  // Originating Identities tab (?from=) so breadcrumb / post-create navigation
  // returns to it instead of the default "Approved" tab.
  const fromTab = searchParams.get("from") || "";
  const identitiesPath = fromTab ? `/identities?tab=${encodeURIComponent(fromTab)}` : "/identities";

  const { user, dfnsToken } = useContext(RoleContext);
  const isReviewer = useIsReviewer();

  const [displayName, setDisplayName] = useState(searchParams.get("displayName") || "");
  const [walletAddress, setWalletAddress] = useState(searchParams.get("walletAddress") || "");
  const [accountNumber, setAccountNumber] = useState(searchParams.get("accountNumber") || "");
  const [isProcessing, setIsProcessing] = useState(false);

  // Secondary Wallets State (only relevant when SECONDARY_WALLET_ENABLED)
  const [secondaryWallets, setSecondaryWallets] = useState([]);
  const [loadingWallets, setLoadingWallets] = useState(false);
  const [refreshingWallet, setRefreshingWallet] = useState(false);
  const [isWalletModalVisible, setIsWalletModalVisible] = useState(false);
  const [editingWallet, setEditingWallet] = useState(null);
  const [walletForm, setWalletForm] = useState({
    walletAddress: "",
  });
  const userEmail = searchParams.get("email") || null;

  const fetchUserWallets = async () => {
    if (!userEmail) return;

    setLoadingWallets(true);
    try {
      const wallets = await UserService.getUserWallets(userEmail);
      setSecondaryWallets(wallets || []);
    } catch (error) {
      console.error("Error fetching wallets:", error);
      // Don't show error message on initial load, wallets might not exist yet
    } finally {
      setLoadingWallets(false);
    }
  };

  const handleRefreshWallet = async () => {
    if (!userEmail) {
      message.warning("User email not available. Please save the identity first.");
      return;
    }

    setRefreshingWallet(true);
    try {
      const result = await UserService.fetchAndStoreSecondaryWallet(userEmail);

      if (result.noDataFound) {
        message.warning("Secondary wallet record does not exist. Please contact T7X support.");
        // Show instruction to create manually
        Modal.info({
          title: "No Wallet Found",
          content: (
            <div>
              <p>No secondary wallet was found in the system for this user.</p>
              <p className="mt-2">You can manually create secondary wallet information using the "Add Wallet" button below.</p>
            </div>
          ),
        });
      } else if (result.success) {
        message.success(result.message);
        await fetchUserWallets();
      }
    } catch (error) {
      console.error("Error refreshing wallet:", error);
      message.error("Failed to refresh wallet. Please try again.");
    } finally {
      setRefreshingWallet(false);
    }
  };

  // Check for incomplete operations on mount
  useEffect(() => {
    void checkForIncompleteOperation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (SECONDARY_WALLET_ENABLED && userEmail) {
      void fetchUserWallets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  // ---------- Validation ----------
  function validateDigitalID(displayName, walletAddress, accountNumber) {
    if (displayName.trim() === "") {
      toast.error("Identity display Name is required");
      return false;
    }

    if (walletAddress?.trim() === "") {
      toast.error("Investor Wallet Address is required");
      return false;
    }

    if (!isEthereumAddress(walletAddress)) {
      toast.error("Invalid Ethereum Wallet Address in Investor Wallet Address");
      return false;
    }

    if (accountNumber?.trim() === "") {
      toast.error("Investor KYC ID is required");
      return false;
    }

    if (SECONDARY_WALLET_ENABLED && (!secondaryWallets || secondaryWallets.length === 0)) {
      toast.error("At least one secondary wallet is required");
      return false;
    }

    return true;
  }

  // ---------- Error Classification ----------
  function classifyError(error) {
    const errorMsg = error?.message || error?.reason || "";

    if (errorMsg.includes("nonce") || error?.code === "NONCE_EXPIRED") {
      return {
        type: "NONCE_ERROR",
        retryable: true,
        userMessage: "Blockchain sync issue. Please try again after some time.",
      };
    }
    if (errorMsg.includes("gas")) {
      return { type: "GAS_ERROR", retryable: false, userMessage: "Insufficient gas" };
    }
    if (errorMsg.includes("already exists") || errorMsg.includes("duplicate")) {
      return { type: "DUPLICATE", retryable: false, userMessage: "Identity already exists" };
    }
    if (error?.code === 4001 || errorMsg.includes("cancel")) {
      return { type: "USER_CANCELLED", retryable: false, userMessage: "Operation cancelled" };
    }
    if (errorMsg.includes("reject")) {
      return { type: "USER_REJECTED", retryable: false, userMessage: "Operation rejected" };
    }

    return { type: "UNKNOWN", retryable: false, userMessage: errorMsg };
  }

  // Secondary Wallet Management Functions (only relevant when SECONDARY_WALLET_ENABLED)
  const handleAddWallet = () => {
    setEditingWallet(null);
    setWalletForm({
      walletAddress: "",
    });
    setIsWalletModalVisible(true);
  };

  const handleEditWallet = (wallet) => {
    setEditingWallet(wallet);
    setWalletForm({
      walletAddress: wallet.walletAddress,
    });
    setIsWalletModalVisible(true);
  };

  const handleDeleteWallet = async (wallet) => {
    Modal.confirm({
      title: "Unlink Wallet",
      content: "Are you sure you want to unlink this wallet?",
      okText: "Unlink",
      okType: "danger",
      okButtonProps: {
        className: "!bg-red-600 !text-white !border-red-600 hover:!bg-red-600 hover:!text-white hover:!border-red-600",
      },
      onOk: async () => {
        try {
          if (!wallet.id) {
            // Never persisted - it only exists in this form's local state.
            setSecondaryWallets(secondaryWallets.filter((w) => w.walletAddress !== wallet.walletAddress));
            message.success("Wallet removed successfully");
            return;
          }

          // The identity doesn't exist on-chain yet at this stage, so this
          // wallet was only ever saved to the UserWallets table (never linked
          // on-chain). Just delete the record - there is nothing to unlink.
          setIsProcessing(true);
          await UserService.deleteUserWallet(wallet.id);

          message.success("Wallet removed successfully");
          setSecondaryWallets((prev) => prev.filter((w) => w.walletAddress !== wallet.walletAddress));
        } catch (error) {
          console.error("Error removing wallet:", error);
          message.error("Failed to remove wallet");
        } finally {
          setIsProcessing(false);
        }
      },
    });
  };

  const handleSaveWallet = async () => {
    if (!walletForm.walletAddress) {
      message.error("Wallet Address is required");
      return;
    }

    if (!isEthereumAddress(walletForm.walletAddress)) {
      message.error("Invalid Ethereum Wallet Address");
      return;
    }

    const isDuplicate = secondaryWallets.some((wallet) => {
      if (editingWallet && wallet.walletAddress === editingWallet.walletAddress) {
        return false;
      }
      return wallet.walletAddress.toLowerCase() === walletForm.walletAddress.toLowerCase();
    });

    if (isDuplicate) {
      message.error("This wallet address already exists");
      return;
    }

    try {
      if (editingWallet) {
        const updatedWallets = secondaryWallets.map((w) => (w === editingWallet ? { ...walletForm } : w));
        setSecondaryWallets(updatedWallets);
        message.success("Wallet updated locally");
      } else {
        setSecondaryWallets([...secondaryWallets, { ...walletForm }]);
        message.success("Wallet added locally");
      }

      setIsWalletModalVisible(false);
      setEditingWallet(null);
    } catch (error) {
      console.error("Error saving wallet:", error);
      message.error("Failed to save wallet");
    }
  };

  // ---------- LocalStorage State Persistence ----------
  function saveOperationState(walletAddr, state) {
    const stateKey = `identity-creation-${walletAddr.toLowerCase()}`;
    try {
      localStorage.setItem(
        stateKey,
        JSON.stringify({
          ...state,
          timestamp: Date.now(),
        })
      );
    } catch (e) {
      console.error("Failed to save state:", e);
    }
  }

  function loadOperationState(walletAddr) {
    const stateKey = `identity-creation-${walletAddr.toLowerCase()}`;
    try {
      const saved = localStorage.getItem(stateKey);
      if (!saved) return null;

      const state = JSON.parse(saved);
      // Ignore states older than 1 hour
      if (Date.now() - state.timestamp > 3600000) {
        localStorage.removeItem(stateKey);
        return null;
      }

      return state;
    } catch (e) {
      console.error("Failed to load state:", e);
      return null;
    }
  }

  function clearOperationState(walletAddr) {
    const stateKey = `identity-creation-${walletAddr.toLowerCase()}`;
    try {
      localStorage.removeItem(stateKey);
    } catch (e) {
      console.error("Failed to clear state:", e);
    }
  }

  async function checkForIncompleteOperation() {
    if (!walletAddress) return;

    const savedState = loadOperationState(walletAddress);
    if (savedState && !savedState.completed) {
      const shouldResume = window.confirm("An incomplete identity creation was detected. Would you like to resume?");

      if (shouldResume) {
        setDisplayName(savedState.displayName || displayName);
        setAccountNumber(savedState.accountNumber || accountNumber);
        toast.info('Ready to resume identity creation. Click "Create Digital Id" to continue.');
      } else {
        clearOperationState(walletAddress);
      }
    }
  }

  // ---------- Indexer-aware helpers (built on the shared utility) ----------

  /**
   * Wait until getIdentityFn(walletAddress) returns a non-zero identity.
   * Throws if not indexed within the configured attempts.
   */
  const waitForIdentityOnChain = async (walletAddr, getIdentityFn, toastId) => {
    return waitForIndexer(
      async () => {
        const result = await getIdentityFn(walletAddr);
        return result && result !== ZERO_ADDRESS ? result : null;
      },
      {
        resourceName: `Identity for ${walletAddr}`,
        maxAttempts: 12,
        onAttempt: (attempt, max) => {
          if (toastId) {
            toast.update(toastId, {
              render: `Waiting for identity on-chain... (${attempt}/${max})`,
            });
          }
        },
      }
    );
  };

  /**
   * Wait until service.isVerified(walletAddress) returns true.
   * Used after registration to confirm on-chain registry inclusion.
   */
  const waitForRegistrationOnChain = async (walletAddr, toastId) => {
    return waitForIndexer(
      async () => {
        const isRegistered = await service.isVerified(walletAddr);
        return isRegistered ? true : null;
      },
      {
        resourceName: `Registration of ${walletAddr}`,
        maxAttempts: 12,
        onAttempt: (attempt, max) => {
          if (toastId) {
            toast.update(toastId, {
              render: `Confirming on-chain registration... (${attempt}/${max})`,
            });
          }
        },
      }
    );
  };

  /**
   * Helper to handle USER_CANCELLED / USER_REJECTED toast updates uniformly.
   * Returns true if the error was a user cancel/reject (and should be re-thrown).
   */
  const handleUserAbort = (errMsgOrError, toastId, action) => {
    const errorInfo = classifyError(typeof errMsgOrError === "string" ? { message: errMsgOrError } : errMsgOrError);

    if (errorInfo.type === "USER_CANCELLED") {
      toast.update(toastId, {
        render: `${action} cancelled`,
        type: "warning",
        isLoading: false,
        autoClose: 3000,
      });
      return "USER_CANCELLED";
    }
    if (errorInfo.type === "USER_REJECTED") {
      toast.update(toastId, {
        render: `${action} rejected`,
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
      return "USER_REJECTED";
    }
    return null;
  };

  // Save secondary wallets to database after identity creation (only called when SECONDARY_WALLET_ENABLED)
  const saveSecondaryWallets = async (email) => {
    if (secondaryWallets.length === 0) return;

    const toastId = toast.loading("Saving secondary wallets...");

    try {
      for (const wallet of secondaryWallets) {
        await UserService.saveUserWallet(email, wallet);
      }

      toast.update(toastId, {
        render: `${secondaryWallets.length} secondary wallet(s) saved successfully`,
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error saving secondary wallets:", error);
      toast.update(toastId, {
        render: "Warning: Some secondary wallets may not have been saved",
        type: "warning",
        isLoading: false,
        autoClose: 5000,
      });
    }
  };

  // Send the approval email on an admin approval, after the identity is
  // created and confirmed indexed.
  const notifyApproved = async () => {
    if (!userEmail) {
      toast.warning("Identity approved, but no email is on file — the user was not notified.");
      return false;
    }

    try {
      const result = await UserService.sendApprovalEmail(userEmail);
      if (result?.sent) {
        return true;
      }
      // Cloud function returned without sending (e.g. user-not-found).
      toast.warning(
        `Identity approved, but the approval email was not sent${result?.reason ? ` (${result.reason})` : ""}. Please contact the Nomyx team.`
      );
      return false;
    } catch (e) {
      console.error("Approval email notification failed:", e);
      toast.warning(`Identity approved, but the approval email failed to send: ${e?.message || "unknown error"}. Please contact the Nomyx team.`);
      return false;
    }
  };

  // ---------- Entry Point ----------
  const handleCreateDigitalId = async () => {
    const trimmedDisplayName = displayName.trim();
    const trimmedAccountNumber = accountNumber.trim();

    if (!validateDigitalID(trimmedDisplayName, walletAddress, trimmedAccountNumber)) {
      return;
    }

    if (isProcessing) {
      toast.warning("Operation already in progress");
      return;
    }

    setIsProcessing(true);

    try {
      await processManagedWallet(trimmedDisplayName, trimmedAccountNumber);
    } catch (error) {
      const errorInfo = classifyError(error);
      if (errorInfo.type !== "USER_CANCELLED" && errorInfo.type !== "USER_REJECTED") {
        console.error("Unexpected error in digital ID flow:", error);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // ---------- MANAGED wallet flow ----------
  const processManagedWallet = async (trimmedDisplayName, trimmedAccountNumber) => {
    let toastId = null;
    const walletAddr = walletAddress.toLowerCase();

    try {
      toastId = toast.loading("Processing Digital Identity...");

      // Load any saved state
      const savedState = loadOperationState(walletAddr);
      let identityCreatedOrExists = savedState?.identityCreated || false;
      let identityRegisteredOrExists = savedState?.identityRegistered || false;
      let identity = savedState?.identity || null;

      // Save initial state
      saveOperationState(walletAddr, {
        displayName: trimmedDisplayName,
        accountNumber: trimmedAccountNumber,
        identityCreated: identityCreatedOrExists,
        identityRegistered: identityRegisteredOrExists,
        identity,
        completed: false,
      });

      // ---------- Step 1: Check if identity already exists on-chain ----------
      if (!identity || identity === ZERO_ADDRESS) {
        try {
          // Use waitForIndexer with low attempts as a "check, but don't insist".
          // DfnsService.getIdentity reads via the backend's dfnsGetIdentity cloud
          // function (own RPC connection) rather than service.getIdentity, which
          // needs a browser signer this managed/DFNS-wallet session doesn't have.
          identity = await waitForIndexer(
            async () => {
              const result = await DfnsService.getIdentity(walletAddress);
              return result && result !== ZERO_ADDRESS ? result : null;
            },
            { resourceName: `Existing identity check ${walletAddress}`, maxAttempts: 2 }
          ).catch(() => null);

          if (identity && identity !== ZERO_ADDRESS) {
            console.log("Existing identity found:", identity);
            identityCreatedOrExists = true;
            saveOperationState(walletAddr, {
              displayName: trimmedDisplayName,
              accountNumber: trimmedAccountNumber,
              identityCreated: true,
              identityRegistered: identityRegisteredOrExists,
              identity,
              completed: false,
            });
          }
        } catch (error) {
          console.log("No existing identity found, will create a new one");
        }
      }

      // ---------- Step 2: Create identity if it doesn't exist ----------
      if (!identityCreatedOrExists) {
        console.log("Creating new identity...");
        toast.update(toastId, { render: "Creating identity..." });

        let createInitResponse;
        try {
          const secondaryWalletAddresses = SECONDARY_WALLET_ENABLED ? secondaryWallets.map((wallet) => wallet.walletAddress.toLowerCase()) : [];
          const initResult = await DfnsService.initiateCreateIdentity(walletAddress, user.walletId, dfnsToken, secondaryWalletAddresses);
          if (initResult?.error) throw new Error(initResult.error);
          createInitResponse = initResult.initiateResponse;
        } catch (err) {
          const abortType = handleUserAbort(err, toastId, "Identity creation");
          if (abortType) throw new Error(abortType);

          const msg = err?.reason || err?.message || "Identity creation failed";
          toast.update(toastId, {
            render: msg,
            type: "error",
            isLoading: false,
            autoClose: 6000,
          });
          throw err; // STOP. Don't poll.
        }

        try {
          const completeResult = await DfnsService.completeCreateIdentity(
            user.walletId,
            dfnsToken,
            createInitResponse.challenge,
            createInitResponse.requestBody
          );
          if (completeResult?.error) throw new Error(completeResult.error);
        } catch (err) {
          const abortType = handleUserAbort(err, toastId, "Identity creation");
          if (abortType) throw new Error(abortType);

          const msg = err?.reason || err?.message || "Identity creation failed";
          toast.update(toastId, {
            render: msg,
            type: "error",
            isLoading: false,
            autoClose: 6000,
          });
          throw err; // STOP. Don't poll.
        }

        // Broadcast succeeded — now poll for the identity on-chain via the backend
        // (DfnsService.getIdentity → dfnsGetIdentity cloud function), since this
        // managed/DFNS-wallet session has no browser signer for service.getIdentity
        // to reconnect to.
        identity = await waitForIdentityOnChain(walletAddress, DfnsService.getIdentity, toastId);
        console.log("New identity created:", identity);
        identityCreatedOrExists = true;

        saveOperationState(walletAddr, {
          displayName: trimmedDisplayName,
          accountNumber: trimmedAccountNumber,
          identityCreated: true,
          identityRegistered: identityRegisteredOrExists,
          identity,
          completed: false,
        });
      } else {
        console.log("Identity already exists:", identity);
        toast.update(toastId, { render: "Identity found, proceeding..." });
      }

      // ---------- Step 3 & 4: Register identity in Blockchain (Identity Registry) ----------
      if (!identityRegisteredOrExists) {
        console.log("Registering identity in Blockchain...");
        toast.update(toastId, { render: "Registering identity on blockchain..." });

        // --- DFNS broadcast: must succeed before we wait for indexer ---
        let addIdentityInitResponse;
        try {
          const initResult = await DfnsService.initiateAddIdentity(walletAddress, identity, user.walletId, dfnsToken);
          if (initResult?.error) throw new Error(initResult.error);
          addIdentityInitResponse = initResult.addIdentityInitResponse;
        } catch (err) {
          const abortType = handleUserAbort(err, toastId, "Identity registration");
          if (abortType) throw new Error(abortType);

          const msg = err?.reason || err?.message || "Identity registration failed";
          toast.update(toastId, {
            render: msg,
            type: "error",
            isLoading: false,
            autoClose: 6000,
          });
          throw err; // STOP. No polling, no retries.
        }

        let completeResponse;
        try {
          const completeResult = await DfnsService.completeAddIdentity(
            user.walletId,
            dfnsToken,
            addIdentityInitResponse.challenge,
            addIdentityInitResponse.requestBody
          );
          if (completeResult?.error) throw new Error(completeResult.error);
          completeResponse = completeResult.addIdentityCompleteResponse;
        } catch (err) {
          const abortType = handleUserAbort(err, toastId, "Identity registration");
          if (abortType) throw new Error(abortType);

          const msg = err?.reason || err?.message || "Identity registration failed";
          toast.update(toastId, {
            render: msg,
            type: "error",
            isLoading: false,
            autoClose: 6000,
          });
          throw err; // STOP. No polling, no retries.
        }

        // --- Broadcast succeeded. NOW it's safe to wait for the indexer. ---
        console.log("Broadcast successful, waiting for on-chain confirmation:", completeResponse);
        await waitForRegistrationOnChain(walletAddress, toastId);

        console.log("Identity registered in Blockchain successfully");
        identityRegisteredOrExists = true;

        saveOperationState(walletAddr, {
          displayName: trimmedDisplayName,
          accountNumber: trimmedAccountNumber,
          identityCreated: identityCreatedOrExists,
          identityRegistered: true,
          identity,
          completed: false,
        });
      }

      // ---------- Steps 5 & 6 are handled by a server-side trigger for managed wallets ----------
      // (metadata update + user approval). Frontend just needs to mark complete.

      saveOperationState(walletAddr, {
        displayName: trimmedDisplayName,
        accountNumber: trimmedAccountNumber,
        identityCreated: true,
        identityRegistered: true,
        identity,
        completed: true,
      });

      if (SECONDARY_WALLET_ENABLED && secondaryWallets.length > 0 && userEmail) {
        await saveSecondaryWallets(userEmail);
      }

      toast.update(toastId, {
        render: "Digital Identity processed successfully",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });

      await notifyApproved();

      clearOperationState(walletAddr);
      navigate("/identities");
    } catch (error) {
      const errorInfo = classifyError(error);

      if (errorInfo.type === "USER_CANCELLED" || errorInfo.type === "USER_REJECTED") {
        // Keep saved state so user can resume
        return;
      }

      console.error("Error in managed wallet flow:", error);

      if (toastId) {
        let errorMessage = error?.reason || error?.message || "An error occurred while processing Digital Identity";

        if (errorInfo.type === "NONCE_ERROR") {
          errorMessage = "Blockchain sync issue detected. Please try again in a moment.";
        }

        toast.update(toastId, {
          render: errorMessage,
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });
      } else {
        toast.error(error?.reason || error?.message || "An error occurred while processing Digital Identity");
      }

      throw error;
    }
  };

  // ---------- Render ----------
  return (
    <div>
      <Breadcrumb
        className="bg-transparent"
        items={[
          {
            title: <Link to={"/"}>Home</Link>,
          },
          {
            title: <Link to={identitiesPath}>Identities</Link>,
          },
          {
            title: "Add",
          },
        ]}
      />
      <p className="text-xl p-6">Create Digital Id</p>
      <hr />
      <div className="p-6 mt-2">
        <div>
          <label htmlFor="identityName">Identity display name *</label>
          <div className="mt-3 relative w-full flex border rounded-lg">
            <Input
              id="identityName"
              value={displayName}
              className="border w-full p-2 rounded-lg text-xl"
              placeholder="Enter Display Name"
              type="text"
              maxLength={32}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={isProcessing}
            />
            <p className="absolute right-5 top-3">{displayName.length}/32</p>
          </div>
          <p>User-friendly name that describes the trusted issuers. Shown to end-users</p>
        </div>
        <div className="mt-10 mb-6 w-[100%] max-[600px]:w-full border p-6 rounded-lg">
          <div>
            <label htmlFor="investorWalletAddress">Investor Wallet Address</label>
            <div className="mt-2 relative w-full flex border rounded-lg">
              <Input
                id="walletAddress"
                value={walletAddress}
                className="border w-full p-2 rounded-lg text-3xl"
                placeholder="Enter Wallet Address"
                type="text"
                onChange={(e) => setWalletAddress(e.target.value.trim())}
                disabled={isProcessing}
              />
            </div>
          </div>
          <div className="mt-6">
            <label htmlFor="investorAccountNumber">Investor KYC ID Provider Account Number</label>
            <div className="mt-2 relative w-full flex border rounded-lg">
              <Input
                id="investorAccountNumber"
                value={accountNumber}
                className="border w-full p-2 rounded-lg text-3xl"
                placeholder="Enter Account number"
                type="text"
                onChange={(e) => setAccountNumber(e.target.value)}
                disabled={isProcessing}
              />
            </div>
          </div>
        </div>

        {/* Secondary Wallets Section */}
        {SECONDARY_WALLET_ENABLED && (
          <div className="mt-6 mb-6 w-[100%] max-[600px]:w-full border p-6 rounded-lg">
            <div className="flex justify-between items-center mb-4">
              <div>
                <p className="text-lg font-semibold">
                  Secondary Wallets <span className="text-red-500">*</span>
                </p>
                <p className="text-sm text-gray-500">
                  {userEmail ? "Manage additional wallets for this identity" : "Add wallets locally. They will be saved after identity creation."}
                </p>
              </div>
              {!isReviewer && (
                <div className="flex gap-2">
                  {userEmail && secondaryWallets.length === 0 && !loadingWallets && (
                    <Button type="default" icon={<ReloadOutlined />} loading={refreshingWallet} onClick={handleRefreshWallet} disabled={isProcessing}>
                      Fetch Secondary Wallet
                    </Button>
                  )}
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleAddWallet} className="bg-[#9952b3]" disabled={isProcessing}>
                    Add Wallet
                  </Button>
                </div>
              )}
            </div>

            {loadingWallets ? (
              <div className="text-center py-8">Loading wallets...</div>
            ) : secondaryWallets.length === 0 ? (
              <div className="border rounded-xl p-6 bg-gray-50 text-center">
                <p className="text-gray-500 mb-2">No secondary wallets found</p>
                <p className="text-sm text-gray-400">
                  {userEmail
                    ? "Click 'Fetch Secondary Wallet' to fetch from the system or 'Add Wallet' to create manually"
                    : "Click 'Add Wallet' to add secondary wallets for this identity"}
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {secondaryWallets.map((wallet, index) => (
                  <div key={wallet.walletAddress || index} className="border rounded-xl p-4 bg-white">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold">Wallet Address:</span>
                          <span className="text-gray-600 font-mono text-sm">{wallet.walletAddress}</span>
                          {!wallet.walletAddress && <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Not saved yet</span>}
                        </div>
                      </div>
                      {!isReviewer && (
                        <div className="flex gap-2">
                          <Button type="text" icon={<EditOutlined />} onClick={() => handleEditWallet(wallet)} disabled={isProcessing} />
                          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDeleteWallet(wallet)} disabled={isProcessing} />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!isReviewer && (
          <div className="flex justify-end max-[600px]:justify-center">
            <Button
              onClick={handleCreateDigitalId}
              className="nomyx-id-button max-[600px]:w-[60%] min-w-max text-center font-semibold h-11"
              disabled={isProcessing}
            >
              {isProcessing ? "Processing..." : "Create Digital Id"}
            </Button>
          </div>
        )}
      </div>

      {/* Wallet Modal */}
      {SECONDARY_WALLET_ENABLED && (
        <Modal
          title={editingWallet ? "Edit Secondary Wallet" : "Add Secondary Wallet"}
          open={isWalletModalVisible}
          onOk={handleSaveWallet}
          onCancel={() => {
            setIsWalletModalVisible(false);
            setEditingWallet(null);
          }}
          okText={editingWallet ? "Update" : "Add"}
          okButtonProps={{ className: "bg-[#9952b3]" }}
        >
          <div className="flex flex-col gap-4 mt-4">
            <div>
              <label htmlFor="modalWalletAddress" className="block mb-2">
                Wallet Address <span className="text-red-500">*</span>
              </label>
              <Input
                id="modalWalletAddress"
                value={walletForm.walletAddress}
                onChange={(e) => setWalletForm({ ...walletForm, walletAddress: e.target.value })}
                placeholder="Enter wallet address (0x...)"
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default CreateDigitalId;
